/** Projections over device presence: who is here and what can run where. */

import type { HarnessPresence, ProjectMount } from './types'

/**
 * Liveness window (RFC 0261 slice 2 / RFC 0240): a device unseen for longer than
 * this reads as absent. 30s matches the existing responder lease TTL, so a
 * device heartbeating on the ~10s cadence stays present with margin.
 */
export const DEFAULT_PRESENCE_TTL_MS = 30_000
/** One present device as a placement picker sees it (RFC 0261 slice 2). */
export interface DevicePresenceRef {
  deviceId: string
  name: string
  kind?: string
  defaultModel?: string
  /** The agent harnesses this device can drive (RFC 0269), so a picker on ANY
   *  surface can offer "run this on my laptop's Claude Code" without opening
   *  the device or reaching a localhost endpoint. */
  harnesses?: HarnessPresence[]
  /** The projects this device serves, and where each lives on it (RFC 0290
   *  §3b). Absent means the device has not said, which reads as "serves
   *  nothing here" and never as "serves everything". */
  mounts?: ProjectMount[]
  /** Millis since this device's last heartbeat, at the `now` passed in — lets a
   *  UI show "5s ago" without recomputing against a clock it doesn't control. */
  lastSeenAgoMs: number
}
/**
 * Is a device present as of `now`?
 *
 * The whole liveness model in one predicate: present iff it heartbeated within
 * the window. There is NO stored online/offline flag to disagree with — a device
 * that stopped beating is gone the moment its last beat ages out. That is what
 * makes a placement picker safe: it can only ever offer a device that is beating
 * right now, never one that has quietly died.
 */
export function isDevicePresent(
  lastSeen: unknown,
  now: number,
  ttlMs = DEFAULT_PRESENCE_TTL_MS,
): boolean {
  return typeof lastSeen === 'number' && now - lastSeen <= ttlMs
}
/**
 * Project a presence doc into the devices present as of `now`, newest-seen
 * first. Accepts the raw doc snapshot; returns `[]` for an absent/empty
 * registry, so a deployment with no devices yet renders empty rather than
 * throwing. Devices outside the liveness window are filtered out entirely — a
 * caller never has to reason about staleness.
 */
export function projectPresentDevices(
  presenceDoc: unknown,
  now: number,
  ttlMs = DEFAULT_PRESENCE_TTL_MS,
): DevicePresenceRef[] {
  if (!presenceDoc || typeof presenceDoc !== 'object') return []
  const devices = (presenceDoc as { devices?: unknown }).devices
  if (!devices || typeof devices !== 'object') return []

  const out: DevicePresenceRef[] = []
  for (const [deviceId, raw] of Object.entries(
    devices as Record<string, unknown>,
  )) {
    if (!raw || typeof raw !== 'object') continue
    const dev = raw as {
      name?: unknown
      kind?: unknown
      lastSeen?: unknown
      defaultModel?: unknown
      harnesses?: unknown
      mounts?: unknown
    }
    if (!isDevicePresent(dev.lastSeen, now, ttlMs)) continue
    out.push({
      deviceId,
      name: String(dev.name ?? ''),
      ...(typeof dev.kind === 'string' ? { kind: dev.kind } : {}),
      ...(typeof dev.defaultModel === 'string'
        ? { defaultModel: dev.defaultModel }
        : {}),
      ...(Array.isArray(dev.harnesses)
        ? { harnesses: dev.harnesses as HarnessPresence[] }
        : {}),
      ...(Array.isArray(dev.mounts)
        ? { mounts: dev.mounts as ProjectMount[] }
        : {}),
      lastSeenAgoMs: now - (dev.lastSeen as number),
    })
  }
  return out.sort((a, b) => a.lastSeenAgoMs - b.lastSeenAgoMs)
}
/**
 * The present devices that mount `projectId` (RFC 0290 §3d).
 *
 * A project is offerable on a surface if some device that is beating RIGHT NOW
 * says it serves that project. Availability is not a stored flag: it is derived
 * from presence, so it expires with the device rather than needing anything to
 * clear it. There is no "available" bit anywhere that can disagree with whether
 * a machine is actually there.
 *
 * Takes a raw presence doc, like `projectPresentDevices`, so a caller holds one
 * document and asks it several questions.
 */
export function hostsFor(
  presenceDoc: unknown,
  projectId: string,
  now: number,
  ttlMs = DEFAULT_PRESENCE_TTL_MS,
): DevicePresenceRef[] {
  return projectPresentDevices(presenceDoc, now, ttlMs).filter((device) =>
    (device.mounts ?? []).some((mount) => mount.projectId === projectId),
  )
}
/**
 * Whether a project can be offered, and why not when it cannot.
 *
 * THREE STATES, NOT TWO. `unknown` is the one that matters and the one a
 * boolean would have lost. `mounts` is optional on `DevicePresence`, so a
 * device running a daemon older than RFC 0290 S2 publishes none, and reading
 * that as "serves nothing" would grey out every project in the World and leave
 * a person unable to start any work at all. Gating therefore engages only once
 * SOMETHING on the fleet speaks mounts; until then the honest answer is that
 * this surface cannot tell, and a control that cannot tell must not refuse.
 *
 * The asymmetry is deliberate. Wrongly offering a project costs a session that
 * waits for a worker; wrongly refusing every project costs the whole app.
 */
export type ProjectAvailability = 'available' | 'unavailable' | 'unknown'
export function projectAvailability(
  presenceDoc: unknown,
  projectId: string,
  now: number,
  ttlMs = DEFAULT_PRESENCE_TTL_MS,
): ProjectAvailability {
  return availabilityIn(
    projectPresentDevices(presenceDoc, now, ttlMs),
    projectId,
  )
}
/**
 * The same rule, over devices a caller has ALREADY projected.
 *
 * This is where the three states are actually decided, and it exists so they
 * are decided in one place. A surface holding a live device list (a React hook
 * subscribed to presence, say) would otherwise re-implement the `unknown`
 * case, and that is precisely the branch that must not drift: get it wrong in
 * the second copy and the whole picker greys out against an older daemon.
 *
 * Callers must pass devices already filtered for liveness, as
 * `projectPresentDevices` returns them. A dead device's mounts are not an
 * offer.
 */
export function availabilityIn(
  devices: readonly DevicePresenceRef[],
  projectId: string,
): ProjectAvailability {
  if (
    devices.some((d) => (d.mounts ?? []).some((m) => m.projectId === projectId))
  )
    return 'available'
  // Absent mounts is "has not said", never "serves nothing": only a device that
  // HAS said makes this a refusal.
  if (devices.some((d) => d.mounts !== undefined)) return 'unavailable'
  return 'unknown'
}
